<template>
  <ODialog
    ref="dialog"
    :title="$t('components.giftCard.actions.delete.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('giftCard.delete'), type: 'confirm', variant: 'danger', callback: handleConfirm },
    ]"
    :loading="loading"
    :portal="portal"
  />
</template>

<script setup lang="ts">
import type { GiftCard } from '~/_api/models/giftCard'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification, $bus } = useNuxtApp()

const dialog = ref()
const giftCard = ref<GiftCard | null>(null)

const info = computed(() => {
  return $i18n.t('components.giftCard.actions.delete.info', {
    number: giftCard.value?.customNumber || giftCard.value?.number,
  })
})

const open = (giftCardItem: GiftCard) => {
  giftCard.value = giftCardItem
  dialog.value.open()
}

const handleConfirm = async () => {
  if (!giftCard.value) return

  emit('update:loading', true)

  try {
    const response = await $api.giftCard.delete(giftCard.value)

    if (response.success) {
      emit('success')

      $bus.emit.deleted('GiftCard', giftCard.value)

      $notification.toast({
        title: $i18n.t('components.giftCard.actions.delete.success'),
        text: $i18n.t('components.giftCard.actions.delete.successInfo'),
        variant: 'success',
      })

      return true
    }
  } catch (e) {
    $notification.toast({
      title: $i18n.t('general.error'),
      text: $i18n.t('components.giftCard.actions.delete.errorInfo'),
    })
  }

  emit('update:loading', false)
}

defineExpose({
  open,
})
</script>
